"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Icon } from "@/lib/icons/Icon";
import { toast } from "@/lib/notifications/toast";
import type {
  CShopCartConfig,
  CShopCartTotal,
  CShopDeliveryOption,
  CShopInpostParcelDetails,
  CShopInpostParcelSelection,
  CShopInpostPoint,
} from "@/lib/api/cms-catalog";
import type { CShopTranslations } from "./CShop.i18n";
import styles from "./CShopInpostPicker.module.css";

/**
 * CShopInpostPicker - widget wyboru paczkomatu InPost renderowany pod opcjami
 * dostawy w widoku koszyka (`c_shop` action=`card`) gdy wybrana dostawa jest
 * inpost'owa (`config.delivery_option.integration === "inpost"`).
 *
 * Zewnetrzny panel pokazuje aktualnie wybrany paczkomat (lub info "nie wybrano")
 * + button "Wybierz paczkomat" / "Zmien" / "Usun". Klikniecie "wybierz" otwiera
 * native `<dialog>` modal z:
 *   - autocomplete po nazwie / adresie (endpoint `?action=search&q=...`)
 *   - button "Znajdz najblizszy paczkomat" (Geolocation API + `?action=nearest`)
 *   - mapa Google Maps z markerami (SDK ladowany z klucza z `content.google_api_key`;
 *     gdy brak klucza fallback do listy autocomplete bez mapy)
 *   - button "Zatwierdz" -> POST `/api/c-shop/update-card` z `inpost_parcel_name`
 *
 * Dane paczkomatow pochodza z lokalnej tabeli synchronizowanej cron'em - nie wolamy
 * ShipX z fronta.
 */
export function CShopInpostPicker({
  cardId,
  selection,
  googleApiKey,
  hasValidationError,
  t,
  onChanged,
}: CShopInpostPickerProps) {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <section className={styles.root} aria-label={t.inpost.heading}>
      <h3 className={styles.heading}>
        <Icon name="map-pin" />
        {t.inpost.heading}
      </h3>

      {selection !== null ? (
        <SelectedParcelView selection={selection} />
      ) : (
        <div className={styles.notSelected}>{t.inpost.noneSelected}</div>
      )}

      {hasValidationError && selection === null && (
        <div className={styles.errorMessage} role="status">
          {t.inpost.gotoOrderRequired}
        </div>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.nearestBtn}
          onClick={() => setModalOpen(true)}
        >
          {selection !== null ? t.inpost.change : t.inpost.select}
        </button>
      </div>

      {modalOpen && (
        <CShopInpostPickerModal
          cardId={cardId}
          selection={selection}
          googleApiKey={googleApiKey}
          t={t}
          onClose={() => setModalOpen(false)}
          onChanged={onChanged}
        />
      )}
    </section>
  );
}

export type CShopInpostPickerProps = {
  cardId: number | null;
  selection: CShopInpostParcelSelection | null;
  googleApiKey: string | null;
  /** Czy przycisk "Przejdz do zamawiania" byl juz klikniety bez wybranego
      paczkomatu - wtedy pokazujemy pod widget'em komunikat walidacji. */
  hasValidationError: boolean;
  t: CShopTranslations;
  onChanged: (
    config: CShopCartConfig,
    total: CShopCartTotal,
    deliveryOptions: CShopDeliveryOption[] | null,
  ) => void;
};

/* ─── Panel zewnetrzny (wybrany paczkomat + button zmien) ──────────────── */

function SelectedParcelView({
  selection,
}: {
  selection: CShopInpostParcelSelection;
}) {
  return (
    <div className={styles.selected}>
      <span className={styles.selectedName}>{selection.name}</span>
      <span className={styles.selectedAddress}>{selection.address}</span>
      {selection.location_description !== "" && (
        <span className={styles.selectedDescription}>
          ({selection.location_description})
        </span>
      )}
    </div>
  );
}

/* ─── Modal picker ─────────────────────────────────────────────────────── */

type ApiResult<T> = { ok: true } & T;

function CShopInpostPickerModal({
  cardId,
  selection,
  googleApiKey,
  t,
  onClose,
  onChanged,
}: {
  cardId: number | null;
  /** Aktualnie zapisany paczkomat (z koszyka). Modal pre-loaduje detale przez
      `byName` przy mount'cie i ustawia jako `pendingSelection` - user wchodzi
      z opcja "zatwierdz ponownie" albo swobodnie zmienia na inny. */
  selection: CShopInpostParcelSelection | null;
  googleApiKey: string | null;
  t: CShopTranslations;
  onClose: () => void;
  onChanged: CShopInpostPickerProps["onChanged"];
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const mapCanvasRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<CShopInpostParcelDetails[]>([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [nearestBusy, setNearestBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [pendingSelection, setPendingSelection] =
    useState<CShopInpostParcelDetails | null>(null);
  /* Komunikat bledu wewnatrz modala (na gorze body). Toast jest zaslaniany przez
     backdrop modala, wiec dla akcji wykonywanych z wewnatrz pickera (nearest,
     geolokalizacja, ladowanie danych) pokazujemy komunikat tutaj. Toast zostaje
     tylko dla saveFailed (mutacja koszyka moze byc kontynuowana poza modalem). */
  const [modalError, setModalError] = useState<string | null>(null);

  /* Native <dialog> - otwarcie przez showModal() gwarantuje focus trap + escape +
     inertness reszty strony. Bez tego musielibysmy sami trapowac Tab i handle'owac
     Escape (patrz ConfirmDialog dla wzoru). */
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (!d.open) d.showModal();
  }, []);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    const onCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    d.addEventListener("cancel", onCancel);
    return () => d.removeEventListener("cancel", onCancel);
  }, [onClose]);

  const onBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) onClose();
  };

  /* ─── Autocomplete: debounce fetch po search query ─── */
  useEffect(() => {
    const q = searchQuery.trim();
    if (q === "") {
      setSearchResults([]);
      setSearchBusy(false);
      return;
    }
    setSearchBusy(true);
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/c-shop/inpost?mode=search&q=${encodeURIComponent(q)}`,
          { cache: "no-store" },
        );
        if (!res.ok) throw new Error("search failed");
        const body = (await res.json()) as ApiResult<{
          items: CShopInpostParcelDetails[];
        }>;
        if (cancelled) return;
        setSearchResults(body.items ?? []);
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearchBusy(false);
      }
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchQuery]);

  /* ─── Google Maps SDK - lazy loading + init mapy z markerami ─── */
  const mapCtx = useGoogleMap({
    apiKey: googleApiKey,
    canvasRef: mapCanvasRef,
    onError: () => setMapError(t.inpost.mapFailed),
    onMarkerClick: (name) => {
      /* Klik markera -> pobierz detale (adres) + oznacz jako pending. */
      void loadParcelByName(name).then((details) => {
        if (details !== null) setPendingSelection(details);
      });
    },
  });

  /* Wysrodkuj mape na aktualnym wyborze / pending gdy pozycja jest znana +
     podswietl odpowiadajacy marker (accent color pin zamiast stock red). */
  useEffect(() => {
    if (mapCtx === null) return;
    const target = pendingSelection;
    mapCtx.highlightMarker(target?.name ?? null);
    if (target !== null) {
      mapCtx.panTo(target.lat, target.lng, 15);
    }
  }, [mapCtx, pendingSelection]);

  /* ─── Akcje ─── */

  const loadParcelByName = useCallback(
    async (name: string): Promise<CShopInpostParcelDetails | null> => {
      try {
        const res = await fetch(
          `/api/c-shop/inpost?mode=byName&name=${encodeURIComponent(name)}`,
          { cache: "no-store" },
        );
        if (!res.ok) throw new Error("byName failed");
        const body = (await res.json()) as ApiResult<{
          item: CShopInpostParcelDetails | null;
        }>;
        return body.item ?? null;
      } catch {
        setModalError(t.inpost.loadFailed);
        return null;
      }
    },
    [t.inpost.loadFailed],
  );

  /* Init: gdy modal otwiera sie z juz zapisanym paczkomatem (button "Zmien"),
     wczytujemy detale przez `byName` (parent ma tylko `CShopInpostParcelSelection`
     bez lat/lng) i ustawiamy jako `pendingSelection`. Istniejacy effect na
     `pendingSelection` zajmie sie panTo + highlight markera po zaladowaniu SDK.
     Guard `cancelled` na wypadek szybkiego zamkniecia modala przed odpowiedzia. */
  useEffect(() => {
    if (selection === null) return;
    let cancelled = false;
    void loadParcelByName(selection.name).then((details) => {
      if (cancelled || details === null) return;
      setPendingSelection(details);
    });
    return () => {
      cancelled = true;
    };
    /* Init tylko na mount modala - `selection` w cyklu zycia jednego modala
       jest stale (parent zamyka/otwiera nowy przy zmianie). `loadParcelByName`
       ma dep na `t.inpost.loadFailed` ktore tez sie nie zmienia w modalu. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const findNearest = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setModalError(t.inpost.geolocationUnsupported);
      return;
    }
    setModalError(null);
    setNearestBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const res = await fetch(
            `/api/c-shop/inpost?mode=nearest&lat=${latitude}&lng=${longitude}`,
            { cache: "no-store" },
          );
          if (!res.ok) throw new Error("nearest failed");
          const body = (await res.json()) as ApiResult<{
            item: CShopInpostParcelDetails | null;
          }>;
          //`?? null` normalizuje undefined (gdy backend nie zwroci pola `item`
          //z jakiegokolwiek powodu) na null - bez tego pusty rezultat leciat by
          //do setPendingSelection jako undefined i renderowanie przechodzilo przez
          //`pendingSelection !== null` gard'a (undefined !== null jest true).
          const item = body.item ?? null;
          if (item === null) {
            setModalError(t.inpost.nearestNotFound);
            return;
          }
          setPendingSelection(item);
        } catch {
          setModalError(t.inpost.loadFailed);
        } finally {
          setNearestBusy(false);
        }
      },
      () => {
        setModalError(t.inpost.geolocationDenied);
        setNearestBusy(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, [
    t.inpost.geolocationDenied,
    t.inpost.geolocationUnsupported,
    t.inpost.loadFailed,
    t.inpost.nearestNotFound,
  ]);

  const confirmSelection = useCallback(async () => {
    if (cardId === null || pendingSelection === null || saving) return;
    setSaving(true);
    try {
      const res = await fetch("/api/c-shop/update-card", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          card_id: cardId,
          inpost_parcel_name: pendingSelection.name,
        }),
      });
      if (!res.ok) throw new Error("update failed");
      const data = (await res.json()) as {
        ok?: boolean;
        config: CShopCartConfig;
        total: CShopCartTotal;
        delivery_options?: CShopDeliveryOption[];
      };
      if (data.ok !== true) throw new Error("update failed");
      onChanged(data.config, data.total, data.delivery_options ?? null);
      router.refresh();
      onClose();
    } catch {
      toast.error(t.inpost.saveFailed);
    } finally {
      setSaving(false);
    }
  }, [
    cardId,
    onChanged,
    onClose,
    pendingSelection,
    router,
    saving,
    t.inpost.saveFailed,
  ]);

  /* Kliknieciw wynik autocomplete oznacza pending selection - clear komunikatu
     bledu (user reagowal, komunikat przestal byc aktualny). */
  const pickResult = useCallback((item: CShopInpostParcelDetails) => {
    setModalError(null);
    setPendingSelection(item);
  }, []);

  /* Wpisywanie w search czysci komunikat bledu (user reagowal na komunikat). */
  const onSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setModalError(null);
    setSearchQuery(e.target.value);
  }, []);

  const canConfirm = pendingSelection !== null && !saving;

  const searchTrimmed = searchQuery.trim();

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onClick={onBackdropClick}
      aria-labelledby="inpostPickerTitle"
    >
      <div className={styles.dialogContent}>
        <header className={styles.dialogHeader}>
          <h2 id="inpostPickerTitle" className={styles.dialogTitle}>
            {t.inpost.modalTitle}
          </h2>
          <button
            type="button"
            className={styles.dialogCloseBtn}
            onClick={onClose}
            aria-label={t.inpost.modalClose}
          >
            <Icon name="x" />
          </button>
        </header>

        <div className={styles.searchRow}>
          <input
            className={styles.searchInput}
            type="search"
            value={searchQuery}
            onChange={onSearchChange}
            placeholder={t.inpost.searchPlaceholder}
            aria-label={t.inpost.searchAriaLabel}
          />
          <button
            type="button"
            className={styles.nearestBtn}
            onClick={findNearest}
            disabled={nearestBusy}
          >
            <Icon name="crosshair" />
            {nearestBusy ? t.inpost.nearestBusy : t.inpost.nearest}
          </button>
        </div>

        {modalError !== null && (
          <div className={styles.modalError} role="status">
            <Icon name="warning-circle" />
            <span>{modalError}</span>
            <button
              type="button"
              className={styles.modalErrorClose}
              onClick={() => setModalError(null)}
              aria-label={t.inpost.modalClose}
            >
              <Icon name="x" />
            </button>
          </div>
        )}

        <div className={styles.body}>
          <div
            className={styles.results}
            aria-label={t.inpost.listResultsAriaLabel}
          >
            {pendingSelection !== null && (
              /* Sticky karta na gorze listy - "wybrany paczkomat" widoczny zawsze
                 (rowniez gdy user scrolluje wyniki lub gdy klikniety marker nie
                 pasuje do aktualnej frazy wyszukiwania). Dolny preview bar zostal
                 usuniety - selekcja siedzi teraz w tym samym miejscu co reszta
                 aktywnych elementow (accent border matchuje marker na mapie). */
              <div className={styles.selectedCard} role="status">
                <div className={styles.selectedCardHeader}>
                  <span className={styles.selectedCardLabel}>
                    <Icon name="check-circle" />
                    {t.inpost.selectedHeading}
                  </span>
                  <button
                    type="button"
                    className={styles.selectedCardClear}
                    onClick={() => setPendingSelection(null)}
                    aria-label={t.inpost.selectedClear}
                  >
                    <Icon name="x" />
                  </button>
                </div>
                <span className={styles.selectedCardName}>
                  {pendingSelection.name}
                </span>
                <span className={styles.selectedCardAddress}>
                  {pendingSelection.label}
                </span>
              </div>
            )}
            {searchTrimmed === "" ? (
              pendingSelection === null && (
                <p className={styles.resultsEmpty}>{t.inpost.searchHint}</p>
              )
            ) : searchBusy ? (
              <p className={styles.resultsLoading}>{t.inpost.searchLoading}</p>
            ) : searchResults.length === 0 ? (
              <p className={styles.resultsEmpty}>{t.inpost.searchNoResults}</p>
            ) : (
              <ul className={styles.resultsList}>
                {searchResults.map((item) => (
                  <li key={item.name}>
                    <button
                      type="button"
                      className={styles.resultItem}
                      onClick={() => pickResult(item)}
                      data-active={
                        pendingSelection?.name === item.name ? "true" : "false"
                      }
                    >
                      <span className={styles.resultName}>{item.name}</span>
                      <span className={styles.resultAddress}>{item.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={styles.mapWrap}>
            <div ref={mapCanvasRef} className={styles.mapCanvas} />
            {googleApiKey === null && (
              <div className={styles.mapUnavailable}>
                {t.inpost.mapUnavailable}
              </div>
            )}
            {mapError !== null && (
              <div className={styles.mapUnavailable}>{mapError}</div>
            )}
          </div>
        </div>

        <footer className={styles.dialogFooter}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={saving}
          >
            {t.inpost.cancel}
          </button>
          <button
            type="button"
            className={styles.confirmBtn}
            onClick={() => void confirmSelection()}
            disabled={!canConfirm}
          >
            {t.inpost.confirm}
          </button>
        </footer>
      </div>
    </dialog>
  );
}

/* ─── Google Maps loader (lazy, per instance) ──────────────────────────── */

type GoogleMapHandle = {
  panTo: (lat: number, lng: number, zoom?: number) => void;
  /** Podswietl marker paczkomatu (accent color + wiekszy scale + wyzszy z-index).
      `null` = wyczysc podswietlenie (poprzednio podswietlony wraca do defaultu). */
  highlightMarker: (name: string | null) => void;
};

/**
 * Laduje Google Maps JS SDK (jesli klucz dostepny) + inicjalizuje mape z markerami
 * ze wszystkich paczkomatow (endpoint `?action=points`). Klik w marker wola
 * `onMarkerClick` z nazwa paczkomatu.
 *
 * Ladowanie SDK jest ONE-SHOT per klucz (nawet gdy modal jest otwierany wielokrotnie)
 * - `window.google.maps` pozostaje po pierwszym imporcie i kolejne modale reuse'uja.
 * Bledy ladowania sygnalizowane przez `onError` (widget pokazuje fallback).
 */
function useGoogleMap({
  apiKey,
  canvasRef,
  onError,
  onMarkerClick,
}: {
  apiKey: string | null;
  canvasRef: React.RefObject<HTMLDivElement | null>;
  onError: () => void;
  onMarkerClick: (name: string) => void;
}): GoogleMapHandle | null {
  const [handle, setHandle] = useState<GoogleMapHandle | null>(null);
  /* markerClickRef trzyma najnowszy callback zeby marker listener nie musial byc
     ponownie tworzony przy kazdym re-renderze (7k markerow = 7k listener'ow). */
  const markerClickRef = useRef(onMarkerClick);
  useEffect(() => {
    markerClickRef.current = onMarkerClick;
  }, [onMarkerClick]);

  useEffect(() => {
    if (apiKey === null || apiKey === "") return;
    if (canvasRef.current === null) return;

    let cancelled = false;

    (async () => {
      try {
        await loadGoogleMapsSdk(apiKey);
        if (cancelled) return;

        const canvas = canvasRef.current;
        if (canvas === null) return;

        const google = (window as unknown as { google: GoogleMapsNamespace })
          .google;

        const map = new google.maps.Map(canvas, {
          zoom: 6,
          center: { lat: 52.065162, lng: 19.252522 },
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        /* Pobieramy markery per otwarcie mapy - lista jest globalna dla domeny,
           zbior nie zmienia sie miedzy modalami tego samego usera (cron aktualizuje
           raz na 24h), wiec cache po stronie Next.js by nie zaszkodzil. Na razie
           bez cache - if performance bites, do dodania (fetch bez `no-store` +
           `revalidate` per godzine).

           MarkerClusterer laduje sie rownolegle z fetch'em - te same ~7k paczkomatow
           bez klastrowania zalewa cala Polske czerwonymi pinami i mapa jest
           bezuzyteczna (patrz issue #<...>). */
        const [pointsRes] = await Promise.all([
          fetch("/api/c-shop/inpost?mode=points", { cache: "no-store" }),
          loadMarkerClustererSdk(),
        ]);
        if (!pointsRes.ok) throw new Error("points fetch failed");
        const body = (await pointsRes.json()) as ApiResult<{
          points: CShopInpostPoint[];
        }>;
        if (cancelled) return;

        /* NIE ustawiamy `map` na Marker - MarkerClusterer zarzadza widocznoscia
           (dodaje/usuwa marker z mapy w zaleznosci od zoom levelu i klastra do
           ktorego marker aktualnie nalezy). Z `map` ustawionym markery renderuja
           sie dodatkowo poza klastrami = duplikacja + brak clusteringu efektu. */
        const markersByName = new Map<
          string,
          {
            setIcon: (icon: unknown) => void;
            setZIndex: (z: number | null) => void;
          }
        >();

        /* Default marker icon - branded InPost pin (unifikacja z widokiem admina
           `m_shop_orders_inpost_colorbox_options_default.imp` gdzie ten sam PNG
           lezy w `/ADMIN/template/default/gfx/parcel_locker-active.png`). Kopia
           w `public/images/inpost-parcel-locker.png` (41x50 natural), anchor
           (20, 50) = bottom-center pinu. */
        const defaultMarkerIcon = {
          url: "/images/inpost-parcel-locker.png",
          scaledSize: new google.maps.Size(41, 50),
          anchor: new google.maps.Point(20, 50),
        };

        /* Icon dla pending selection - custom SVG pin w accent color (matchuje
           kolor `confirmBtn` z modula CSS). `#4a90e2` = fallback tokena
           `--color-accent`. Anchor (12, 32) = bottom-center 24x32 SVG viewBox.
           Trzymamy w scope hooka zeby restore po odznaczeniu zwracal do PNG
           InPosta, nie do stock red pin'a Google (defaultMarkerIcon vs null). */
        const selectedIcon = {
          path: "M12 0C5.4 0 0 5.4 0 12c0 3.4 1.4 6.4 3.7 8.6L12 32l8.3-11.4C22.6 18.4 24 15.4 24 12 24 5.4 18.6 0 12 0zm0 17c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5z",
          fillColor: "#4a90e2",
          fillOpacity: 1,
          strokeColor: "#2b6dad",
          strokeWeight: 1.5,
          scale: 1.6,
          anchor: new google.maps.Point(12, 32),
        };

        const markers = (body.points ?? []).map((p) => {
          const marker = new google.maps.Marker({
            position: { lat: p.lat, lng: p.lng },
            title: p.name,
            icon: defaultMarkerIcon,
          });
          marker.addListener("click", () => {
            markerClickRef.current(p.name);
          });
          markersByName.set(p.name, marker);
          return marker;
        });

        if (cancelled) return;

        const clustererNs = (
          window as unknown as { markerClusterer: MarkerClustererNamespace }
        ).markerClusterer;
        new clustererNs.MarkerClusterer({
          map,
          markers,
          /* Custom cluster renderer - ciemna navy tarcza z bialym licznikiem
             (unifikacja z widokiem admina; stock renderer dawal jasno-niebieskie
             kolka SVG ktore odbijaly sie od czerni pinu InPost). SVG-path buduje
             pelne kolo o promieniu 22 (44px srednicy), font-size skalowany do
             ilosci cyfr zeby licznik zmiescil sie w kole. */
          renderer: {
            render: ({ count, position }) => {
              return new google.maps.Marker({
                position: position as { lat: number; lng: number },
                icon: {
                  path: "M 0 0 m -22 0 a 22 22 0 1 0 44 0 a 22 22 0 1 0 -44 0",
                  fillColor: "#1e2a3a",
                  fillOpacity: 0.95,
                  strokeColor: "#0f1622",
                  strokeWeight: 1,
                  scale: 1,
                  anchor: new google.maps.Point(0, 0),
                },
                label: {
                  text: String(count),
                  color: "#ffffff",
                  fontWeight: "700",
                  fontSize:
                    count < 100 ? "14px" : count < 1000 ? "12px" : "10px",
                },
                zIndex: 1000 + count,
              });
            },
          },
        });

        /* highlightedName trzymane w closure hooka - nie potrzebujemy React state
           bo highlight jest czysto wizualnym efektem na markerze (setIcon mutuje
           SDK'ova instance), a zmiana nie powinna re-renderowac React tree.
           setState uzywane tylko do publikacji handle'a rodzicowi (setHandle). */
        let highlightedName: string | null = null;

        setHandle({
          panTo: (lat, lng, zoom) => {
            map.setCenter({ lat, lng });
            if (typeof zoom === "number") map.setZoom(zoom);
          },
          highlightMarker: (name) => {
            if (highlightedName !== null && highlightedName !== name) {
              const prev = markersByName.get(highlightedName);
              /* Restore do brandowego PNG (nie do stock red pin przez null).
                 setZIndex(null) -> default stacking (paczkomat wraca do klastra). */
              prev?.setIcon(defaultMarkerIcon);
              prev?.setZIndex(null);
            }
            highlightedName = name;
            if (name !== null) {
              const next = markersByName.get(name);
              /* Wysoki z-index zeby wybrany marker byl nad sasiednimi po zoom-in. */
              next?.setIcon(selectedIcon);
              next?.setZIndex(1000);
            }
          },
        });
      } catch {
        if (!cancelled) onError();
      }
    })();

    return () => {
      cancelled = true;
    };
    /* apiKey/canvas nie zmieniaja sie w cyklu zycia jednego modala; onError/onMarkerClick
       przez ref'y wewnetrzne (patrz komentarz przy markerClickRef). */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  return handle;
}

/**
 * Minimum SDK Google Maps potrzebne do renderu mapy + markerow. Deklarujemy locally
 * zamiast dokladac @types/google.maps (pare pol z ogromnego typu, tree-shaking
 * bibliotece niepotrzebny bo SDK laduje sie z CDN).
 */
type GoogleMapsNamespace = {
  maps: {
    Map: new (
      el: HTMLElement,
      opts: {
        zoom: number;
        center: { lat: number; lng: number };
        mapTypeControl?: boolean;
        streetViewControl?: boolean;
        fullscreenControl?: boolean;
      },
    ) => {
      setCenter: (c: { lat: number; lng: number }) => void;
      setZoom: (z: number) => void;
    };
    Marker: new (opts: {
      position: { lat: number; lng: number };
      /* MarkerClusterer zarzadza widocznoscia markerow (patrz komentarz w
         useGoogleMap); poza clustererem `map` bylo wymagane, w scenariuszu
         z clustererem musi byc pominiete. Stad `?`. */
      map?: unknown;
      title?: string;
      icon?: unknown;
      /* `label` uzywane przez cluster renderer (biały licznik na ciemnym kole). */
      label?: {
        text: string;
        color?: string;
        fontWeight?: string;
        fontSize?: string;
      };
      zIndex?: number;
    }) => {
      addListener: (event: string, cb: () => void) => void;
      setIcon: (icon: unknown) => void;
      setZIndex: (z: number | null) => void;
    };
    /* SVG Point dla `anchor` w custom marker icon (highlight pending). */
    Point: new (x: number, y: number) => unknown;
    /* Size dla `scaledSize` w URL-icon (rozmiar renderowanego pinu InPost). */
    Size: new (width: number, height: number) => unknown;
  };
};

/**
 * Minimum globalnego UMD `@googlemaps/markerclusterer` - biblioteka eksponuje
 * `window.markerClusterer.MarkerClusterer`. Nie sciagamy `@types/google.maps` bo
 * `map`/`markers` traktujemy jako `unknown` (klaster nie widzi naszego minimum'a
 * typow `Map`/`Marker`, ale w runtime akceptuje instancje SDK'a bez zastrzezen).
 */
type MarkerClustererNamespace = {
  MarkerClusterer: new (opts: {
    map: unknown;
    markers?: unknown[];
    renderer?: {
      render: (cluster: {
        position: unknown;
        count: number;
        markers: unknown[];
      }) => unknown;
    };
  }) => unknown;
};

/**
 * Deduplikacja ladowania Google Maps JS API - kilka pickerow na stronie / kolejne
 * otwarcia tego samego pickera. Rejestrujemy globalny callback `__initGoogleMaps`,
 * `<script>` doklada sie tylko raz per (klucz + zestaw bibliotek).
 */
let googleMapsPromise: Promise<void> | null = null;

function loadGoogleMapsSdk(apiKey: string): Promise<void> {
  if (googleMapsPromise !== null) return googleMapsPromise;

  //Klucz jest publiczny (osadzany w kodzie strony) - juz sam React-cms server side
  //go dostal z REST i wstrzyknal do content'u. Ograniczenia po stronie Google Console
  //(HTTP referrer + limit zapytan) - patrz komentarz w `IGOOGLE::getApiKey`.
  googleMapsPromise = new Promise((resolve, reject) => {
    if (
      typeof window !== "undefined" &&
      (window as unknown as { google?: { maps?: unknown } }).google?.maps
    ) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&callback=__imperialInitGoogleMaps`;
    script.async = true;
    script.defer = true;

    (window as unknown as { __imperialInitGoogleMaps: () => void }).__imperialInitGoogleMaps = () => {
      resolve();
    };

    script.onerror = () => {
      googleMapsPromise = null; //allow retry next open
      reject(new Error("google maps sdk load failed"));
    };

    document.head.appendChild(script);
  });

  return googleMapsPromise;
}

/**
 * Deduplikacja ladowania `@googlemaps/markerclusterer` (UMD z unpkg) - biblioteka
 * jest zewnetrzna dla naszego bundle'a, dostarczana taka sama sciezka przy kazdym
 * otwarciu modala. Pin wersji zeby unpkg nie serwowal breaking major podczas
 * migracji Google'a. Failure ladowania -> onError w useGoogleMap (map wyswietla
 * fallback "mapa niedostepna").
 */
let markerClustererPromise: Promise<void> | null = null;

function loadMarkerClustererSdk(): Promise<void> {
  if (markerClustererPromise !== null) return markerClustererPromise;

  markerClustererPromise = new Promise((resolve, reject) => {
    if (
      typeof window !== "undefined" &&
      (window as unknown as { markerClusterer?: { MarkerClusterer?: unknown } })
        .markerClusterer?.MarkerClusterer
    ) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src =
      "https://unpkg.com/@googlemaps/markerclusterer@2.5.3/dist/index.min.js";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      markerClustererPromise = null; //allow retry next open
      reject(new Error("marker clusterer sdk load failed"));
    };
    document.head.appendChild(script);
  });

  return markerClustererPromise;
}
